import React, { useState, useEffect } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import { Sparkles, X, Plus, Loader2, ChefHat, Crown } from "lucide-react";
import { useLang } from "@/lib/LanguageContext";
import { useAuth } from "@/lib/AuthContext";
import { recipeAPI } from "@/lib/api";

export default function Generate() {
  const { t, lang } = useLang();
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [input, setInput] = useState("");
  const [selected, setSelected] = useState([]);
  const [popular, setPopular] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showSignupModal, setShowSignupModal] = useState(false);

  useEffect(() => {
    recipeAPI.popularIngredients(lang).then((r) => setPopular(r.data)).catch(() => {});
  }, [lang]);

  useEffect(() => {
    if (location.state?.ingredient) {
      const ing = location.state.ingredient;
      setSelected((prev) => (prev.includes(ing) ? prev : [...prev, ing]));
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  const addIngredient = () => {
    const trimmed = input.trim().toLowerCase();
    if (trimmed && !selected.includes(trimmed)) {
      setSelected([...selected, trimmed]);
    }
    setInput("");
  };

  const removeIngredient = (ing) => {
    setSelected(selected.filter((s) => s !== ing));
  };

  const togglePopular = (name) => {
    if (selected.includes(name)) {
      removeIngredient(name);
    } else {
      setSelected([...selected, name]);
    }
  };

  const handleGenerate = async () => {
    if (selected.length === 0) return;

    // Si non connecté, afficher le modal d'inscription
    if (!user) {
      setShowSignupModal(true);
      return;
    }

    setError("");
    setLoading(true);
    try {
      const langMap = { fr: "fr", en: "en", es: "es", de: "de", pt: "pt", it: "it" };
      const res = await recipeAPI.generate(selected, langMap[lang] || "fr");
      await refreshUser();
      navigate("/recipe", { state: { recipe: res.data } });
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (typeof detail === "string") {
        setError(detail);
      } else {
        setError(t("error"));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      addIngredient();
    }
  };

  const categories = {};
  popular.forEach((ing) => {
    if (!categories[ing.category]) categories[ing.category] = [];
    categories[ing.category].push(ing);
  });

  return (
    <div className="min-h-screen max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-testid="generate-page">
      <div className="text-center mb-10">
        <h1 className="font-heading text-3xl sm:text-4xl tracking-tight font-medium text-white mb-3">
          {t("generateTitle")}
        </h1>
        <p className="text-stone-400">{t("generateSubtitle")}</p>
        {user && !user.is_premium && (
          <p className="mt-2 text-xs text-stone-500">
            {t("freeRecipesUsed")}: {user.free_recipes_used} / 1
          </p>
        )}      </div>

      {/* Input */}
      <div className="flex gap-2 mb-6">
        <input
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          data-testid="ingredient-input"
          placeholder={t("enterIngredient")}
          className="flex-1 px-4 py-3 bg-[#1C1917] border border-white/10 rounded-xl text-white placeholder-stone-600 focus:outline-none focus:border-amber-500/50 focus:ring-1 focus:ring-amber-500/20 transition-colors"
        />
        <button
          onClick={addIngredient}
          data-testid="add-ingredient-btn"
          className="px-4 py-3 bg-amber-500 text-stone-950 rounded-xl font-semibold hover:bg-amber-600 transition-colors"
        >
          <Plus className="w-5 h-5" />
        </button>
      </div>

      {/* Selected */}
      {selected.length > 0 && (
        <div className="mb-8" data-testid="selected-ingredients">
          <p className="text-xs tracking-[0.15em] uppercase font-semibold text-stone-500 mb-3">
            {t("selectedIngredients")} ({selected.length})
          </p>
          <div className="flex flex-wrap gap-2">
            {selected.map((ing) => (
              <span
                key={ing}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-amber-500/10 border border-amber-500/20 text-amber-500 text-sm rounded-full"
              >
                {ing}
                <button onClick={() => removeIngredient(ing)} className="hover:text-amber-300">
                  <X className="w-3.5 h-3.5" />
                </button>
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Popular Ingredients Grid */}
      {Object.entries(categories).map(([cat, ings]) => (
        <div key={cat} className="mb-6">
          <p className="text-xs tracking-[0.15em] uppercase font-semibold text-stone-500 mb-3">
            {t(`cat_${cat}`) || cat}
          </p>
          <div className="flex flex-wrap gap-2">
            {ings.map((ing) => (
              <button
                key={ing.name}
                onClick={() => togglePopular(ing.name)}
                data-testid={`popular-${ing.name}`}
                className={`inline-flex items-center gap-2 px-3 py-2 rounded-xl text-sm border transition-all duration-200 ${
                  selected.includes(ing.name)
                    ? "bg-amber-500/10 border-amber-500/30 text-amber-500"
                    : "bg-[#1C1917] border-white/5 text-stone-400 hover:border-amber-500/20 hover:text-white"
                }`}
              >
                <span>{ing.emoji}</span>
                <span className="capitalize">{ing.name}</span>
              </button>
            ))}
          </div>
        </div>
      ))}

      {/* Error */}
      {error && (
        <div data-testid="generate-error" className="mb-6 bg-red-500/10 border border-red-500/20 text-red-400 text-sm rounded-xl px-4 py-3">
          {error}
        </div>
      )}

      {/* Generate Button */}
      <div className="mt-8">
        <button
          onClick={handleGenerate}
          disabled={loading || selected.length === 0}
          data-testid="generate-recipe-btn"
          className="w-full sm:w-auto mx-auto flex items-center justify-center gap-2 px-8 py-4 bg-amber-500 text-stone-950 font-semibold rounded-full hover:bg-amber-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed text-lg"
        >
          {loading ? (
            <>
              <Loader2 className="w-5 h-5 animate-spin" />
              {t("generating")}
            </>
          ) : (
            <>
              <Sparkles className="w-5 h-5" />
              {t("generateRecipe")}
            </>
          )}
        </button>
      </div>

      {/* Modal inscription pour visiteurs non connectés */}
      {showSignupModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setShowSignupModal(false)} />
          <div className="relative bg-[#1C1917] border border-white/10 rounded-2xl p-8 max-w-md w-full shadow-2xl" data-testid="signup-modal">
            <button
              onClick={() => setShowSignupModal(false)}
              className="absolute top-4 right-4 text-stone-500 hover:text-white"
            >
              <X className="w-5 h-5" />
            </button>

            <div className="text-center">
              <div className="w-16 h-16 rounded-full bg-amber-500/10 flex items-center justify-center mx-auto mb-4">
                <ChefHat className="w-8 h-8 text-amber-500" />
              </div>
              <h3 className="font-heading text-xl font-medium text-white mb-2">
                {"Cr\u00e9ez un compte pour essayer gratuitement votre premi\u00e8re recette"}
              </h3>
              <p className="text-sm text-stone-400 mb-6">
                {"Inscrivez-vous et g\u00e9n\u00e9rez votre premi\u00e8re recette personnalis\u00e9e avec l\u2019IA, c\u2019est gratuit !"}
              </p>

              <div className="bg-amber-500/5 border border-amber-500/20 rounded-xl p-4 mb-6">
                <div className="flex items-center gap-2 justify-center mb-2">
                  <Crown className="w-4 h-4 text-amber-500" />
                  <span className="text-sm font-medium text-amber-500">{"Offre d\u00e9couverte"}</span>
                </div>
                <p className="text-xs text-stone-400">
                  {"1 recette gratuite \u00e0 l\u2019inscription \u2022 Recettes illimit\u00e9es avec l\u2019abonnement Premium"}
                </p>
              </div>

              {/* Ingrédients sélectionnés rappel */}
              {selected.length > 0 && (
                <div className="mb-5">
                  <p className="text-xs text-stone-500 mb-2">{"Vos ingr\u00e9dients s\u00e9lectionn\u00e9s :"}</p>
                  <div className="flex flex-wrap gap-1.5 justify-center">
                    {selected.map((ing) => (
                      <span key={ing} className="bg-stone-800 text-stone-300 text-xs px-2.5 py-1 rounded-full capitalize">
                        {ing}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              <Link
                to="/register"
                data-testid="signup-modal-register"
                className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-amber-500 text-stone-950 font-semibold rounded-full hover:bg-amber-600 transition-all mb-3"
              >
                <Sparkles className="w-5 h-5" />
                {"Cr\u00e9er un compte gratuit"}
              </Link>
              <Link
                to="/login"
                data-testid="signup-modal-login"
                className="block text-sm text-stone-500 hover:text-white transition-colors"
              >
                {"D\u00e9j\u00e0 un compte ? Se connecter"}
              </Link>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
